import { Helmet } from 'react-helmet-async'
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  Link
} from '@mui/material'
import {
  Email,
  AccessTime,
  Visibility,
  GroupWork,
  NotificationsActive,
  Security,
  ArrowForward,
  CheckCircle,
  VerifiedUser,
  CloudQueue,
  LocationOn,
  Shield
} from '@mui/icons-material'

const MARKETPLACE_URL = 'https://marketplace.microsoft.com/en-us/product/taylin-studio.helpdesk-saas'

const BRAND_BLUE = '#00B4D8'
const BRAND_BLUE_DARK = '#0077B6'

const features = [
  {
    icon: <Email />,
    title: 'Every site gets its own email',
    description: 'Automatic email-to-ticket routing per org and site. No complex rules.'
  },
  {
    icon: <AccessTime />,
    title: 'SLA tracking built in',
    description: 'Response and resolution deadlines with breach alerts. No add-on fees.'
  },
  {
    icon: <Visibility />,
    title: 'Client IT managers get visibility',
    description: "Oversight users see tickets across their org's sites without seeing other clients."
  },
  {
    icon: <GroupWork />,
    title: 'Group tickets for outages',
    description: 'When an issue hits multiple sites, one ticket keeps everyone informed.'
  },
  {
    icon: <NotificationsActive />,
    title: 'Microsoft Teams notifications',
    description: 'Real-time ticket alerts in your existing Teams channels.'
  },
  {
    icon: <Security />,
    title: 'Enterprise security, SMB price',
    description: 'Azure Front Door + WAF, mandatory MFA, penetration tested. From £25/agent/month.'
  }
]

const trustBadges = [
  { icon: <CloudQueue sx={{ fontSize: 15 }} />, label: 'Available on Azure Marketplace' },
  { icon: <VerifiedUser sx={{ fontSize: 15 }} />, label: 'Penetration Tested' },
  { icon: <Shield sx={{ fontSize: 15 }} />, label: 'Cyber Essentials Ready' },
  { icon: <LocationOn sx={{ fontSize: 15 }} />, label: 'UK-Hosted' }
]

const pricingBullets = [
  'Annual plan: 12 months for the price of 10',
  'Unlimited organisations, sites, and tickets',
  '14-day free trial — no credit card required'
]

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>Taylin Helpdesk — Purpose-Built Ticketing for MSPs</title>
        <meta
          name="description"
          content="Taylin Helpdesk: purpose-built ticketing for MSPs supporting multi-site clients. Unlimited organisations, per-agent pricing from £25/month. 14-day free trial."
        />
        <meta property="og:title" content="Taylin Helpdesk — MSP Ticketing Done Right" />
        <meta property="og:description" content="Unlimited client orgs and sites. One per-agent price. Built by an MSP, for MSPs." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://helpdesk.taylindigital.com" />
        <link rel="canonical" href="https://helpdesk.taylindigital.com" />
      </Helmet>

      {/* ── Section 1: Hero ── */}
      <Box sx={{ bgcolor: '#fff', pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.025em',
                color: '#0a0a0a',
                mb: 3
              }}
            >
              Stop Paying Per-Organisation<br />for Your MSP Helpdesk
            </Typography>

            <Typography
              variant="h5"
              component="p"
              sx={{
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                fontWeight: 400,
                color: '#555',
                lineHeight: 1.65,
                maxWidth: 660,
                mx: 'auto',
                mb: 5
              }}
            >
              Taylin Helpdesk gives you unlimited client organisations and child sites for one
              per-agent price. Purpose-built for MSPs who support multi-site customers.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ mb: 4 }}
            >
              <Button
                variant="contained"
                size="large"
                href="/signup"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_BLUE,
                  '&:hover': { bgcolor: BRAND_BLUE_DARK },
                  px: 4,
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 700,
                  borderRadius: 2,
                  boxShadow: `0 4px 16px ${BRAND_BLUE}55`
                }}
              >
                Start Your 14-Day Free Trial
              </Button>
              <Link
                href={MARKETPLACE_URL}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  color: '#555',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                See It on Azure Marketplace →
              </Link>
            </Stack>

            {/* Trust bar */}
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              gap={1}
            >
              {trustBadges.map((badge) => (
                <Chip
                  key={badge.label}
                  icon={badge.icon}
                  label={badge.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: '#ddd',
                    color: '#666',
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiChip-icon': { color: '#888' }
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      <Divider />

      {/* ── Section 2: Origin Story ── */}
      <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 3,
              color: '#0a0a0a'
            }}
          >
            Built by an MSP, for MSPs
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1.05rem', md: '1.15rem' },
              lineHeight: 1.8,
              color: '#444',
              maxWidth: 720
            }}
          >
            We built Taylin Helpdesk because we had the same problem you do. Our client, City of London
            Libraries, has 6 branches — each needing its own ticket queue, its own support email, its own
            reporting. Generic helpdesks forced us to choose between paying per-organisation or cramming
            everything into one flat inbox. So we built something better.
          </Typography>
        </Container>
      </Box>

      {/* ── Section 3: Three-Tier Visual ── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 6,
              textAlign: 'center',
              color: '#0a0a0a'
            }}
          >
            Your MSP → Your Clients → Their Sites
          </Typography>

          {/* Tier Diagram */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0}
            alignItems="stretch"
            justifyContent="center"
            sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
          >
            {[
              {
                label: 'Your MSP',
                sublabel: 'Tenant',
                desc: 'You — one platform, one login, full control',
                color: BRAND_BLUE_DARK,
                bg: `${BRAND_BLUE_DARK}12`
              },
              {
                label: 'Your Clients',
                sublabel: 'Organisations',
                desc: 'Each client has their own isolated space',
                color: BRAND_BLUE,
                bg: `${BRAND_BLUE}12`
              },
              {
                label: 'Their Sites',
                sublabel: 'Child Sites',
                desc: 'Each branch, office, or location is separate',
                color: '#48CAE4',
                bg: '#48CAE412'
              }
            ].map((tier, idx, arr) => (
              <Stack key={tier.label} direction="row" alignItems="stretch" sx={{ flex: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    px: 3,
                    py: 3.5,
                    bgcolor: tier.bg,
                    borderTop: `3px solid ${tier.color}`,
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      color: tier.color,
                      textTransform: 'uppercase',
                      mb: 0.5
                    }}
                  >
                    {tier.sublabel}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.15rem',
                      color: '#0a0a0a',
                      mb: 1
                    }}
                  >
                    {tier.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>
                    {tier.desc}
                  </Typography>
                </Box>
                {idx < arr.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'center',
                      px: 0.5,
                      color: '#aaa',
                      fontSize: '1.25rem',
                      userSelect: 'none'
                    }}
                  >
                    →
                  </Box>
                )}
              </Stack>
            ))}
          </Stack>

          {/* Three benefit statements */}
          <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 800, mx: 'auto' }}>
            {[
              { text: 'Unlimited client organisations — no per-org fees' },
              { text: 'Unlimited child sites — each with its own email and queue' },
              { text: '£25/agent/month — that\'s it. Everything included.' }
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item.text}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircle sx={{ color: BRAND_BLUE, fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.95rem', color: '#333', fontWeight: 500, lineHeight: 1.5 }}>
                    {item.text}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Section 4: Feature Highlights ── */}
      <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                fontWeight: 700,
                color: '#0a0a0a',
                mb: 1.5
              }}
            >
              Everything You Need, Nothing You Don't
            </Typography>
            <Typography sx={{ color: '#666', fontSize: '1.05rem' }}>
              Six capabilities that matter to MSPs — all included in one price
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature) => (
              <Grid item xs={12} sm={6} md={4} key={feature.title}>
                <Card
                  sx={{
                    height: '100%',
                    border: '1px solid #e8e8e8',
                    boxShadow: 'none',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: BRAND_BLUE,
                      boxShadow: `0 4px 20px ${BRAND_BLUE}22`,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        bgcolor: `${BRAND_BLUE}18`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: BRAND_BLUE
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '1rem', color: '#0a0a0a' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Section 5: Social Proof ── */}
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 5,
              textAlign: 'center',
              color: '#0a0a0a'
            }}
          >
            Trusted in Production
          </Typography>

          {/* Case Study Card */}
          <Card
            sx={{
              border: `1px solid ${BRAND_BLUE}44`,
              boxShadow: 'none',
              borderRadius: 2,
              mb: 5
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '10px',
                    bgcolor: `${BRAND_BLUE}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <LocationOn sx={{ color: BRAND_BLUE, fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      color: '#0a0a0a',
                      mb: 0.5
                    }}
                  >
                    City of London Libraries
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#888', mb: 1.5 }}>
                    Public sector · 6 branches · London
                  </Typography>
                  <Typography sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.975rem' }}>
                    Supporting 6 library branches across the City of London, each with independent ticket
                    queues and dedicated support emails. Branch managers see their own tickets; the central
                    IT team sees everything — with no per-site fees.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Azure Marketplace Badge */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#888', mb: 2, fontWeight: 500 }}>
              AVAILABLE ON
            </Typography>
            <Link
              href={MARKETPLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  '&:hover': {
                    borderColor: BRAND_BLUE,
                    bgcolor: `${BRAND_BLUE}08`
                  },
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
              >
                <CloudQueue sx={{ color: BRAND_BLUE, fontSize: 22 }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                    Get it from
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0a0a0a', lineHeight: 1.2 }}>
                    Microsoft Azure Marketplace
                  </Typography>
                </Box>
              </Box>
            </Link>
          </Box>

          {/* Testimonial placeholder */}
          <Box
            sx={{
              border: '1px dashed #ddd',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: '#fafafa'
            }}
          >
            <Typography sx={{ color: '#bbb', fontSize: '0.875rem' }}>
              Customer testimonials coming soon
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Section 6: Pricing ── */}
      <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="sm">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 5,
              textAlign: 'center',
              color: '#0a0a0a'
            }}
          >
            One Plan. Everything Included.
          </Typography>

          <Card
            sx={{
              border: `2px solid ${BRAND_BLUE}`,
              boxShadow: `0 8px 32px ${BRAND_BLUE}22`,
              borderRadius: 3,
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: { xs: 4, md: 5 } }}>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '3rem', fontWeight: 800, color: '#0a0a0a', lineHeight: 1 }}>
                  £25
                </Typography>
                <Typography sx={{ color: '#666', fontSize: '1rem' }}>
                  / agent / month
                </Typography>
              </Stack>
              <Typography sx={{ color: '#888', fontSize: '0.875rem', mb: 3 }}>
                Billed monthly · cancel anytime
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2} sx={{ mb: 4 }}>
                {pricingBullets.map((item) => (
                  <Stack key={item} direction="row" spacing={1.5} alignItems="center">
                    <CheckCircle sx={{ color: BRAND_BLUE, fontSize: 18, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.975rem', color: '#333' }}>
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                variant="contained"
                size="large"
                href="/signup"
                fullWidth
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_BLUE,
                  '&:hover': { bgcolor: BRAND_BLUE_DARK },
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 700,
                  borderRadius: 2,
                  mb: 2
                }}
              >
                Start Free Trial
              </Button>

              <Typography sx={{ textAlign: 'center', fontSize: '0.875rem', color: '#888' }}>
                Or{' '}
                <Link
                  href={MARKETPLACE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ color: BRAND_BLUE }}
                >
                  purchase via Azure Marketplace
                </Link>
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* ── Section 7: Final CTA ── */}
      <Box sx={{ bgcolor: BRAND_BLUE_DARK, py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: '#fff',
              mb: 2.5
            }}
          >
            Ready to simplify your MSP helpdesk?
          </Typography>

          <Button
            variant="contained"
            size="large"
            href="/signup"
            endIcon={<ArrowForward />}
            sx={{
              bgcolor: '#fff',
              color: BRAND_BLUE_DARK,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              px: 5,
              py: 1.75,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: 2,
              mb: 2.5,
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
            }}
          >
            Start Your 14-Day Free Trial
          </Button>

          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
            No credit card required. Cancel anytime. Setup takes under 5 minutes.
          </Typography>
        </Container>
      </Box>
    </>
  )
}
